import React, { useState, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";
import _ from "lodash";
import { useStyles } from "./styles";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear as settingIcon } from "@fortawesome/pro-light-svg-icons";
import { ROUTINE_TYPE, INTERCOM_PROVIDER } from "../../../../constants";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ActionForm from "./ActionForm";
import { useCoreEntityContext } from "../../../../hooks/useCoreEntitySlice";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlags";
import { selectContextEntity } from "../../../../state/slices/CoreEntity";
import { Tooltip } from "@material-ui/core";

export const SettingsActionFormAddEditSchema = Yup.object().shape({
  Device: Yup.array().min(1, "At least one device must be selected"),
  Setting: Yup.string().required("Required"),
  Value: Yup.string().required("Required"),
});

const SettingsActionForm = ({ handleSave, handleCancel, action }) => {
  const classes = useStyles();
  const [entities, setEntities] = useState([]);
  const useCoreEntitySlice = useCoreEntityContext();
  const isUmojoFeatureEnabled = useFeatureFlag('Features.Umojo Call Center Integration');
  const devicesInFacility = useSelector(
    (state) =>
      _.orderBy(
        useCoreEntitySlice
          ? selectContextEntity(state).children
          : state.entities.Context.children,
        ["name"],
        ["asc"]
      ),
    shallowEqual
  );

  const getDevices = () => {
    if (Array.isArray(devicesInFacility)) {
      var devices = devicesInFacility?.map((device) => {
        return { name: device?.name, value: device?.entityid };
      });
      setEntities(devices);
    }
  };

  const formatAndSave = (values) => {
    const switchSetting = {
      payload: {
        Device: values.Device,
        Setting: values.Setting,
        Value: values.Value,
      },
      actionTypeID: ROUTINE_TYPE.SETTINGS,
      order: action?.order ?? 0,
    };
    handleSave(switchSetting);
  };

  const defaults = {
    Device: action?.payload?.Device ?? [],
    Setting: action?.payload?.Setting ?? "",
    Value: action?.payload?.Value ?? "",
  };

  const SETTING_STATUS_OPTIONS = [
    { value: 1, label: "Lane Closed" },
    { value: 2, label: "Disable Door" },
    { value: 3, label: "Intercom Provider" },
  ];
  const VALUE_STATUS_OPTIONS = [
    { value: 1, label: "Open" },
    { value: 2, label: "Closed" },
  ];
  const VALUE_DOOR_STATUS_OPTIONS = [
    { value: 1, label: "Enable" },
    { value: 2, label: "Disable" },
  ];
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: defaults,
    resolver: yupResolver(SettingsActionFormAddEditSchema),
  });
  const selectedSetting = watch("Setting");

  useEffect(() => {
    getDevices();
  }, []);

  const getMenuValueItems = (setting) => {
    let values = [];
    switch (setting) {
      case "Disable Door":
        values = VALUE_DOOR_STATUS_OPTIONS;
        break;
      case "Intercom Provider":
        values = Object.keys(INTERCOM_PROVIDER)
          .filter(provider => {
            if (!isUmojoFeatureEnabled && provider === "Umojo") return false;
            return true;
          })
          .map(provider => ({
            value: INTERCOM_PROVIDER[provider], label: provider,
          }));
        break;
      default:
        values = VALUE_STATUS_OPTIONS;
        break;
    }
    return values.map(({ value, label }) => (
      <MenuItem key={value} value={label}>
        {label}
      </MenuItem>
    ));
  }

  return (
    <>
      <ActionForm
        handleSave={formatAndSave}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        icon={<FontAwesomeIcon icon={settingIcon} />}
        edit={action}
        primaryText={"Settings"}
        secondaryText={"Configuration Changes"}
      >
        <Grid container className={clsx([classes.drawerContainer])}>
          <Grid item xs={12}>
            <Controller
              name="Device"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth variant="outlined">
                  <Grid container>
                    <Grid container item xs={12} sm={4} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        id="Device"
                        className={clsx([classes.deviceLabel])}
                      >
                        Device
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Select
                        {...field}
                        multiple
                        fullWidth
                        displayEmpty
                        data-id={"deviceSelect"}
                        data-testid={"deviceSelect"}
                        value={field.value || []}
                        onChange={(event) => {
                          const selectedValues = event.target.value;
                          field.onChange(selectedValues);
                        }}
                        className={clsx([classes.deviceDropdown])}
                        variant="outlined"
                        inputProps={{
                          id: "device-dropdown",
                        }}
                        error={!!errors.Device}
                        renderValue={(selected) => {
                          if (selected.length === 0) {
                            return <em>Select device(s)</em>;
                          }
                          if (selected.length === 1) {
                            const selectedDevice = entities.find(
                              (d) => d.value === selected[0]
                            );
                            return selectedDevice?.name || "Unknown";
                          }

                          const selectedNames = selected
                            .map(
                              (id) => entities.find((d) => d.value === id)?.name
                            )
                            .filter(Boolean);

                          return (
                            <Tooltip
                              title={selectedNames.join(", ")}
                              classes={{ tooltip: classes.tooltip }}
                            >
                              <span>Multiple devices</span>
                            </Tooltip>
                          );
                        }}
                      >
                        {entities.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Device && (
                        <div className={clsx([classes.selectError])}>
                          {errors.Device.message}
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="Setting"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth FormLabel="Setting" variant="outlined">
                  <Grid container>
                    <Grid container item xs={12} sm={4} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        id="Setting"
                        className={clsx([classes.settingLabel])}
                      >
                        Setting
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        data-id={"settingSelect"}
                        data-testid={"settingSelect"}
                        data-value={field?.value}
                        className={clsx([classes.settingDropdown])}
                        variant="outlined"
                        inputProps={{
                          id: "setting-dropdown",
                        }}
                        error={!!errors.Setting}
                        helperText={errors.Setting && errors.Setting.message}
                      >
                        {SETTING_STATUS_OPTIONS.map((setting) => (
                          <MenuItem key={setting.value} value={setting.label}>
                            {setting.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.Setting && (
                        <div className={clsx([classes.selectError])}>
                          {errors.Setting.message}
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="Value"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth FormLabel="Value" variant="outlined">
                  <Grid container>
                    <Grid container item xs={12} sm={4} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        id="Value"
                        className={clsx([classes.valueLabel])}
                      >
                        Value
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        data-id={"valueSelect"}
                        data-testid={"valueSelect"}
                        data-value={field?.value}
                        className={clsx([classes.valueDropdown])}
                        variant="outlined"
                        inputProps={{
                          id: "value-dropdown",
                        }}
                        error={!!errors.Value}
                        helperText={errors.Value && errors.Value.message}
                      >
                        {getMenuValueItems(selectedSetting)}
                      </Select>
                      {errors.Value && (
                        <div className={clsx([classes.selectError])}>
                          {errors.Value.message}
                        </div>
                      )}
                    </Grid>
                  </Grid>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </ActionForm>
    </>
  );
};
export default SettingsActionForm;
